<template>
  <div>
    <p><small>Rules automatically assign users to this subgroup based on the users attributes.</small></p>

    <div v-if="loading" class="text-center">
      <i class="fa fa-spinner fa-spin fa-2x"></i>
    </div>

    <div class="alert alert-warning" v-if="errorMessage">
      {{ errorMessage }}
    </div>

    <div v-if="!loading">
      <div v-if="rules.length === 0" class="alert alert-info">
        No rules have been created yet. Click "Add Rule" to create one.
      </div>

      <div v-if="rules.length > 0">
        <table class="table table-striped">
          <thead>
            <tr>
              <th>Rule Type</th>
              <th>{{ criteriaColumnHeader }}</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.uuid">
              <td>{{ formatRuleType(rule.rule_type) }}</td>
              <td>
                <span v-if="rule.rule_type === 'school' && rule.rule_config && rule.rule_config.school_uuids && rule.rule_config.school_uuids.length > 0">
                  {{ rule.rule_config.school_uuids.length }} school(s)
                </span>
                <span v-else-if="rule.rule_type === 'title' && rule.rule_config && rule.rule_config.titles && rule.rule_config.titles.length > 0">
                  {{ rule.rule_config.titles.length }} title(s): {{ formatTitles(rule.rule_config.titles) }}
                </span>
                <span v-else class="text-muted">No criteria configured</span>
              </td>
              <td>
                <span :class="rule.enabled ? 'label label-success' : 'label label-default'">
                  {{ rule.enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </td>
              <td>
                <button class="btn btn-xs btn-default" @click="editRule(rule)" :disabled="!canManageSubgroups">
                  Edit
                </button>
                <button
                  class="btn btn-xs"
                  :class="rule.enabled ? 'btn-warning' : 'btn-success'"
                  @click="toggleRuleStatus(rule)"
                  :disabled="!canManageSubgroups"
                >
                  {{ rule.enabled ? 'Disable' : 'Enable' }}
                </button>
                <button class="btn btn-xs btn-danger" @click="deleteRule(rule)" :disabled="!canManageSubgroups">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="form-group">
        <button class="btn btn-primary" @click="showAddRuleForm" :disabled="!canManageSubgroups">
          Add Rule
        </button>
        <button class="btn btn-default" @click="$emit('close')">
          Close
        </button>
      </div>

      <!-- Add/Edit Rule Form -->
      <div v-if="showRuleForm" class="panel panel-default">
        <div class="panel-heading">
          <h4>{{ editingRule ? 'Edit Rule' : 'Add Rule' }}</h4>
        </div>
        <div class="panel-body">
          <form @submit.prevent="saveRule">
            <div class="form-group">
              <div class="checkbox">
                <label>
                  <input type="checkbox" v-model="ruleForm.enabled">
                  Enabled
                </label>
              </div>
              <p class="help-block">When enabled, this rule will automatically assign users to this subgroup.</p>
            </div>


            <div class="form-group">
              <label for="rule_type">Rule Type</label>
              <select id="rule_type" class="form-control" v-model="ruleForm.rule_type" required>
                <option value="school">School-based</option>
                <option value="title">Title-based</option>
              </select>
              <p class="help-block">
                <span v-if="ruleForm.rule_type === 'school'">
                  Users are assigned to this subgroup based on their school assignment.
                </span>
                <span v-else-if="ruleForm.rule_type === 'title'">
                  Users are assigned to this subgroup based on their title/role.
                </span>
              </p>
            </div>

            <!-- School selection (shown for school-based rules) -->
            <div v-if="ruleForm.rule_type === 'school'" class="form-group schools">
              <legend>Schools</legend>
              <div v-if="loadingSchools" class="text-muted">
                <i class="fa fa-spinner fa-spin"></i> Loading schools...
              </div>
              <div v-else>
                <div v-if="schools.length === 0" class="alert alert-warning">
                  No schools found in this domain.
                </div>
                <div v-else>
                  <div class="input-mode-toggle">
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="schoolInputMode === 'list' ? 'btn-primary' : 'btn-default'"
                      @click="switchSchoolInputMode('list')"
                    >List</button>
                    <button
                      type="button"
                      class="btn btn-xs"
                      :class="schoolInputMode === 'paste' ? 'btn-primary' : 'btn-default'"
                      @click="switchSchoolInputMode('paste')"
                    >Paste</button>
                  </div>

                  <!-- List mode -->
                  <div v-if="schoolInputMode === 'list'">
                    <div class="schools-filter-controls">
                      <div class="form-group">
                        <input
                          type="text"
                          class="form-control"
                          v-model="schoolSearchTerm"
                          placeholder="Search schools..."
                          aria-label="Filter schools"
                        >
                      </div>
                      <div class="filter-options">
                        <div class="checkbox">
                          <label>
                            <input type="checkbox" v-model="showSelectedOnly">
                            Show selected only
                          </label>
                        </div>
                        <div class="selected-count">
                          <strong>{{ ruleForm.school_uuids.length }}</strong> of <strong>{{ schools.length }}</strong> schools selected
                        </div>
                      </div>
                    </div>
                    <div v-if="filteredSchools.length === 0" class="text-muted">
                      No schools match your search.
                    </div>
                    <div v-else class="schools-list">
                      <div class="checkbox" v-for="school in filteredSchools" :key="school.uuid">
                        <label>
                          <input
                            type="checkbox"
                            :value="school.uuid"
                            :aria-label="'select school: ' + school.name"
                            v-model="ruleForm.school_uuids"
                          >
                          {{ school.name }}
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Paste mode -->
                  <div v-else class="school-paste-section">
                    <p class="help-block">Paste school names, one per line. Names are matched case-insensitively. Unrecognized names will be shown as warnings.</p>
                    <textarea
                      class="form-control school-paste-input"
                      v-model="schoolPasteInput"
                      placeholder="Paste school names, one per line..."
                      rows="6"
                      aria-label="Paste school names"
                    ></textarea>
                    <button
                      type="button"
                      class="btn btn-default school-paste-apply"
                      @click="applySchoolPaste"
                    >Apply</button>
                    <alert v-if="schoolPasteApplyMessage" type="success">
                      {{ schoolPasteApplyMessage }}
                    </alert>
                    <div v-if="schoolPasteUnmatched.length > 0" class="alert alert-warning school-paste-unmatched">
                      <strong>The following school names were not recognized and have been skipped:</strong>
                      <ul class="school-paste-unmatched-list">
                        <li v-for="name in schoolPasteUnmatched" :key="name">{{ name }}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Title input (shown for title-based rules) -->
            <div v-if="ruleForm.rule_type === 'title'" class="form-group titles">
              <legend>Titles</legend>
              <p class="help-block" id="title-help-text">
                Select titles from domain users or add a custom title. Users with any of these titles will be assigned to this subgroup.
                Matching is case-insensitive and requires an exact match.
              </p>

              <div class="input-mode-toggle">
                <button
                  type="button"
                  class="btn btn-xs"
                  :class="titleInputMode === 'individual' ? 'btn-primary' : 'btn-default'"
                  @click="switchTitleInputMode('individual')"
                >Individual</button>
                <button
                  type="button"
                  class="btn btn-xs"
                  :class="titleInputMode === 'paste' ? 'btn-primary' : 'btn-default'"
                  @click="switchTitleInputMode('paste')"
                >Paste</button>
              </div>

              <div class="title-selector">
                <!-- Loading spinner -->
                <div v-if="loadingTitles" class="text-muted">
                  <i class="fa fa-spinner fa-spin"></i> Loading titles...
                </div>

                <template v-else-if="titleInputMode === 'individual'">
                  <!-- Existing titles from domain users as checkboxes -->
                  <div v-if="existingTitles.length > 0" class="titles-list" role="group" aria-describedby="title-help-text">
                    <div class="checkbox" v-for="title in existingTitles" :key="title">
                      <label>
                        <input
                          type="checkbox"
                          :checked="isTitleSelected(title)"
                          @change="toggleTitle(title)"
                          :aria-label="'Select title: ' + title"
                        >
                        {{ title }}
                      </label>
                    </div>
                  </div>

                  <!-- Custom titles (not from domain users) shown as checked, removable checkboxes -->
                  <div v-if="customSelectedTitles.length > 0" class="titles-list custom-titles-list" role="group" aria-label="Custom titles">
                    <div class="checkbox" v-for="title in customSelectedTitles" :key="title">
                      <label>
                        <input
                          type="checkbox"
                          checked
                          @change="toggleTitle(title)"
                          :aria-label="'Remove custom title: ' + title"
                        >
                        {{ title }} <small class="text-muted">(custom)</small>
                      </label>
                    </div>
                  </div>

                  <!-- Custom title input -->
                  <div class="custom-title-section">
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-control"
                        v-model="customTitleInput"
                        placeholder="Add a custom title..."
                        @keyup.enter="addCustomTitle"
                        aria-label="Custom title input"
                        aria-describedby="title-help-text"
                      >
                      <span class="input-group-btn">
                        <button
                          type="button"
                          class="btn btn-default"
                          @click="addCustomTitle"
                          :disabled="!customTitleInput.trim()"
                        >
                          Add
                        </button>
                      </span>
                    </div>
                    <p v-if="customTitleError" class="text-danger">{{ customTitleError }}</p>
                  </div>
                </template>

                <!-- Paste mode -->
                <div v-else class="title-paste-section">
                  <p class="help-block">Paste titles, one per line. Duplicates (case-insensitive) will be removed automatically.</p>
                  <textarea
                    class="form-control title-paste-input"
                    v-model="titlePasteInput"
                    placeholder="Paste titles, one per line..."
                    rows="6"
                    aria-label="Paste titles"
                  ></textarea>
                  <button
                    type="button"
                    class="btn btn-default title-paste-apply"
                    @click="applyTitlePaste"
                  >Apply</button>
                  <alert v-if="titlePasteApplyMessage" type="success">
                    {{ titlePasteApplyMessage }}
                  </alert>
                </div>

                <!-- Selected titles displayed as removable chips (both modes) -->
                <div v-if="validSelectedTitles.length > 0" class="selected-titles" role="list">
                  <span
                    v-for="title in validSelectedTitles"
                    :key="title"
                    class="title-chip label label-info"
                    role="listitem"
                  >
                    {{ title }}
                    <button
                      type="button"
                      class="btn-close-chip"
                      @click="toggleTitle(title)"
                      :aria-label="'Remove title: ' + title"
                    ><i class="fa fa-times"></i></button>
                  </span>
                </div>
              </div>
            </div>

            <div class="form-group">
              <button type="submit" class="btn btn-primary" :disabled="saving">
                <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> Saving...</span>
                <span v-else>Save</span>
              </button>
              <button type="button" class="btn btn-default" @click="cancelRuleForm" :disabled="saving">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Permissions } from '@/client_auth/permissions';
import { Alert } from 'uiv';

export default {
  name: 'SubgroupRules',
  components: { Alert },
  props: {
    subgroup: {
      type: Object,
      required: true,
    },
    domainUuid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingSchools: false,
      saving: false,
      rules: [],
      schools: [],
      errorMessage: '',
      showRuleForm: false,
      editingRule: null,
      ruleForm: {
        rule_type: 'school',
        school_uuids: [],
        titles: [],
        enabled: true,
      },
      canManageSubgroups: false,
      schoolSearchTerm: '',
      showSelectedOnly: false,
      customTitleInput: '',
      customTitleError: '',
      schoolInputMode: 'list',
      schoolPasteInput: '',
      schoolPasteUnmatched: [],
      schoolPasteApplyMessage: '',
      titleInputMode: 'individual',
      titlePasteInput: '',
      titlePasteApplyMessage: '',
      // Only populated when domainInfo.usersUI is filtered (rare fallback path)
      localDomainUsers: [],
      loadingTitlesFallback: false,
    };
  },
  computed: {
    domainUsers() {
      const usersUI = this.$angularWorld.domainInfo?.usersUI;
      if (!usersUI) {
        // domainInfo not available (e.g. in tests without it) – nothing to show yet
        return [];
      }
      if (usersUI.schoolUuid || usersUI.subgroupUuid) {
        // The shared list is filtered; fall back to a direct fetch for all titles.
        return this.localDomainUsers;
      }
      // Normal path: shared unfiltered list already populated at domain page load.
      return usersUI.users || [];
    },
    loadingTitles() {
      const usersUI = this.$angularWorld.domainInfo?.usersUI;
      if (usersUI && (usersUI.schoolUuid || usersUI.subgroupUuid)) {
        // On the fallback path, track loading from the local fetch
        return this.loadingTitlesFallback;
      }
      return usersUI ? usersUI.loading : false;
    },
    filteredSchools() {
      let filtered = this.schools;

      // Filter by selected only
      if (this.showSelectedOnly) {
        filtered = filtered.filter(school =>
          this.ruleForm.school_uuids.includes(school.uuid)
        );
      }

      // Filter by search term
      if (this.schoolSearchTerm) {
        const searchLower = this.schoolSearchTerm.toLowerCase();
        filtered = filtered.filter(school =>
          school.name.toLowerCase().includes(searchLower)
        );
      }

      return filtered;
    },
    existingTitles() {
      const seen = new Set();
      const unique = [];
      for (const user of this.domainUsers) {
        const raw = user.title;
        if (!raw || !raw.trim()) continue;
        const key = raw.trim().toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          unique.push(raw.trim());
        }
      }
      return unique.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    },
    validSelectedTitles() {
      return this.ruleForm.titles.filter(t => t && t.trim());
    },
    customSelectedTitles() {
      const existingLower = new Set(this.existingTitles.map(t => t.toLowerCase()));
      return this.ruleForm.titles.filter(t => t && t.trim() && !existingLower.has(t.toLowerCase()));
    },
    criteriaColumnHeader() {
      const hasTitleRules = this.rules.some(rule => rule.rule_type === 'title');
      const hasSchoolRules = this.rules.some(rule => rule.rule_type === 'school');
      if (hasTitleRules && hasSchoolRules) {
        return 'Criteria';
      }
      return hasTitleRules ? 'Titles' : 'Schools';
    },
  },
  methods: {
    async fetchRules() {
      this.errorMessage = '';
      this.loading = true;
      try {
        const [schoolRulesResponse, titleRulesResponse] = await Promise.all([
          this.$angularWorld.adminEntitiesApi.listSubgroupRules({
            domain_uuid: this.domainUuid,
            rule_type: 'school',
          }),
          this.$angularWorld.adminEntitiesApi.listSubgroupRules({
            domain_uuid: this.domainUuid,
            rule_type: 'title',
          }),
        ]);
        const schoolRules = schoolRulesResponse.data || [];
        const titleRules = titleRulesResponse.data || [];
        const allRules = [...schoolRules, ...titleRules];
        this.rules = allRules.filter(rule => rule.subgroup_uuid === this.subgroup.uuid);
      } catch (error) {
        console.error('Failed to fetch subgroup rules', error);
        this.errorMessage = 'Failed to fetch subgroup rules';
      } finally {
        this.loading = false;
      }
    },

    async fetchSchools() {
      this.loadingSchools = true;
      try {
        const response = await this.$angularWorld.graph.getDomainSchools(this.domainUuid);
        if (Array.isArray(response)) {
          this.schools = response;
        } else {
          this.schools = [];
          this.errorMessage = 'Failed to fetch schools';
        }
      } catch (error) {
        console.error('Failed to fetch schools', error);
        this.errorMessage = 'Failed to fetch schools';
      } finally {
        this.loadingSchools = false;
      }
    },

    showAddRuleForm() {
      this.editingRule = null;
      this.ruleForm = {
        rule_type: 'school',
        school_uuids: [],
        titles: [],
        enabled: true,
      };
      this.schoolSearchTerm = '';
      this.showSelectedOnly = false;
      this.schoolInputMode = 'list';
      this.schoolPasteInput = '';
      this.schoolPasteUnmatched = [];
      this.schoolPasteApplyMessage = '';
      this.titleInputMode = 'individual';
      this.titlePasteInput = '';
      this.titlePasteApplyMessage = '';
      this.showRuleForm = true;
      if (this.schools.length === 0) {
        this.fetchSchools();
      }
    },

    editRule(rule) {
      this.editingRule = rule;
      const titles = rule.rule_config && rule.rule_config.titles && rule.rule_config.titles.length > 0
        ? [...rule.rule_config.titles]
        : [];
      this.ruleForm = {
        rule_type: rule.rule_type,
        school_uuids: rule.rule_config && rule.rule_config.school_uuids ? [...rule.rule_config.school_uuids] : [],
        titles,
        enabled: rule.enabled,
      };
      this.schoolSearchTerm = '';
      this.showSelectedOnly = false;
      this.schoolInputMode = 'list';
      this.schoolPasteInput = '';
      this.schoolPasteUnmatched = [];
      this.schoolPasteApplyMessage = '';
      this.titleInputMode = 'individual';
      this.titlePasteInput = '';
      this.titlePasteApplyMessage = '';
      this.showRuleForm = true;
      if (rule.rule_type === 'school' && this.schools.length === 0) {
        this.fetchSchools();
      }
    },

    async saveRule() {
      this.errorMessage = '';

      // Validate title rules have at least one non-empty title
      if (this.ruleForm.rule_type === 'title') {
        const validTitles = this.ruleForm.titles.filter(t => t && t.trim() !== '');
        if (validTitles.length === 0) {
          this.errorMessage = 'Please enter at least one title';
          return;
        }
      }

      // Validate school rules have at least one school selected
      if (this.ruleForm.rule_type === 'school' && this.ruleForm.school_uuids.length === 0) {
        this.errorMessage = 'Please select at least one school';
        return;
      }

      this.saving = true;
      try {
        const ruleConfig = this.ruleForm.rule_type === 'school'
          ? { school_uuids: this.ruleForm.school_uuids }
          : { titles: this.ruleForm.titles.filter(t => t && t.trim() !== '') };

        const ruleData = {
          subgroup_uuid: this.subgroup.uuid,
          domain_uuid: this.domainUuid,
          rule_type: this.ruleForm.rule_type,
          rule_config: ruleConfig,
          enabled: this.ruleForm.enabled,
        };

        if (this.editingRule) {
          ruleData.uuid = this.editingRule.uuid;
          await this.$angularWorld.adminEntitiesApi.updateSubgroupRule(ruleData);
        } else {
          await this.$angularWorld.adminEntitiesApi.createSubgroupRule(ruleData);
        }

        this.showRuleForm = false;
        this.editingRule = null;
        await this.fetchRules();
      } catch (error) {
        console.error('Failed to save subgroup rule', error);
        this.errorMessage = 'Failed to save subgroup rule';
      } finally {
        this.saving = false;
      }
    },

    cancelRuleForm() {
      this.showRuleForm = false;
      this.editingRule = null;
      this.schoolInputMode = 'list';
      this.schoolPasteInput = '';
      this.schoolPasteUnmatched = [];
      this.schoolPasteApplyMessage = '';
      this.titleInputMode = 'individual';
      this.titlePasteInput = '';
      this.titlePasteApplyMessage = '';
    },

    async toggleRuleStatus(rule) {
      this.errorMessage = '';
      try {
        const updatedRule = {
          uuid: rule.uuid,
          enabled: !rule.enabled,
          subgroup_uuid: rule.subgroup_uuid,
          domain_uuid: rule.domain_uuid,
          rule_type: rule.rule_type,
          rule_config: rule.rule_config,
        };
        await this.$angularWorld.adminEntitiesApi.updateSubgroupRule(updatedRule);
        await this.fetchRules();
      } catch (error) {
        console.error('Failed to toggle rule status', error);
        this.errorMessage = 'Failed to toggle rule status';
      }
    },

    async deleteRule(rule) {
      this.errorMessage = '';
      // eslint-disable-next-line no-alert
      if (globalThis.confirm(`Are you sure you want to delete this rule?`)) {
        try {
          await this.$angularWorld.adminEntitiesApi.deleteSubgroupRule(rule.uuid);
          await this.fetchRules();
        } catch (error) {
          console.error('Failed to delete subgroup rule', error);
          this.errorMessage = 'Failed to delete subgroup rule';
        }
      }
    },

    async updatePermissions() {
      const hasPermission = await this.$angularWorld.auth.doesUserHavePermission([Permissions.UserOps]);
      this.canManageSubgroups = hasPermission;
    },

    formatRuleType(ruleType) {
      const typeLabels = {
        school: 'School-based',
        title: 'Title-based',
      };
      return typeLabels[ruleType] || ruleType;
    },

    formatTitles(titles) {
      if (!titles || titles.length === 0) {
        return '';
      }
      const formatted = titles.join(', ');
      return formatted.length > 50 ? formatted.substring(0, 50) + '...' : formatted;
    },

    async fetchDomainUsers() {
      // domainInfo.usersUI is already populated at domain page load time.
      // This fallback only runs when usersUI is filtered (by school/subgroup),
      // which is uncommon and means we can't rely on the shared list for titles.
      this.loadingTitlesFallback = true;
      try {
        this.localDomainUsers = await this.$angularWorld.userCache.fetchNormalizedUsers(this.domainUuid, null, null);
      } catch (error) {
        console.error('Failed to fetch domain users for title autocomplete', error);
      } finally {
        this.loadingTitlesFallback = false;
      }
    },

    isTitleSelected(title) {
      const lower = title.toLowerCase();
      return this.ruleForm.titles.some(t => t.toLowerCase() === lower);
    },

    toggleTitle(title) {
      const lower = title.toLowerCase();
      const idx = this.ruleForm.titles.findIndex(t => t.toLowerCase() === lower);
      if (idx === -1) {
        this.ruleForm.titles.push(title);
      } else {
        this.ruleForm.titles.splice(idx, 1);
      }
    },

    switchSchoolInputMode(mode) {
      if (mode === 'paste') {
        // Pre-populate textarea with names of currently selected schools
        const selectedNames = this.ruleForm.school_uuids
          .map(uuid => {
            const school = this.schools.find(s => s.uuid === uuid);
            return school ? school.name : null;
          })
          .filter(Boolean);
        this.schoolPasteInput = selectedNames.join('\n');
      } else {
        this.schoolPasteInput = '';
        this.schoolPasteUnmatched = [];
      }
      this.schoolInputMode = mode;
    },

    switchTitleInputMode(mode) {
      if (mode === 'individual') {
        this.titlePasteInput = '';
      }
      this.titleInputMode = mode;
    },

    applySchoolPaste() {
      const lines = this.schoolPasteInput.split('\n').map(l => l.trim()).filter(Boolean);
      const matchedSet = new Set();
      const unmatched = [];
      for (const name of lines) {
        const school = this.schools.find(s => s.name.toLowerCase() === name.toLowerCase());
        if (school) {
          matchedSet.add(school.uuid);
        } else {
          unmatched.push(name);
        }
      }
      const matched = [...matchedSet];
      this.ruleForm.school_uuids = matched;
      this.schoolPasteUnmatched = unmatched;

      const count = matched.length;
      this.schoolPasteApplyMessage = count === 1 ? '1 school selected.' : `${count} schools selected.`;
      clearTimeout(this._schoolPasteApplyTimer);
      this._schoolPasteApplyTimer = setTimeout(() => { this.schoolPasteApplyMessage = ''; }, 3000);
    },

    applyTitlePaste() {
      const lines = this.titlePasteInput.split('\n').map(l => l.trim()).filter(Boolean);
      const seen = new Set();
      const deduped = [];
      for (const line of lines) {
        const key = line.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          deduped.push(line);
        }
      }
      this.ruleForm.titles = deduped;

      const count = deduped.length;
      this.titlePasteApplyMessage = count === 1 ? '1 title applied.' : `${count} titles applied.`;
      clearTimeout(this._titlePasteApplyTimer);
      this._titlePasteApplyTimer = setTimeout(() => { this.titlePasteApplyMessage = ''; }, 3000);
    },

    addCustomTitle() {
      const trimmed = this.customTitleInput.trim();
      if (!trimmed) return;

      const trimmedLower = trimmed.toLowerCase();

      // Duplicate check against existing domain titles
      const isDomainDuplicate = this.existingTitles.some(t => t.toLowerCase() === trimmedLower);
      if (isDomainDuplicate) {
        this.customTitleError = `"${trimmed}" already exists in the list above. Use the checkbox to select it.`;
        return;
      }

      // Duplicate check against already-selected titles
      const isAlreadySelected = this.ruleForm.titles.some(t => t.toLowerCase() === trimmedLower);
      if (isAlreadySelected) {
        this.customTitleError = `"${trimmed}" is already selected.`;
        return;
      }

      this.ruleForm.titles.push(trimmed);
      this.customTitleInput = '';
      this.customTitleError = '';
    },

  },
  async mounted() {
    await this.fetchRules();
    await this.updatePermissions();
  },
  beforeDestroy() {
    clearTimeout(this._schoolPasteApplyTimer);
    clearTimeout(this._titlePasteApplyTimer);
  },
  watch: {
    '$angularWorld.auth.currentUser'() {
      this.updatePermissions();
    },
    'ruleForm.rule_type'(newType) {
      if (newType === 'school' && this.schools.length === 0) {
        this.fetchSchools();
      }
      if (newType === 'title') {
        const usersUI = this.$angularWorld.domainInfo?.usersUI;
        // Only fetch directly when usersUI is filtered – in that case the shared
        // list is scoped to a school/subgroup and won't contain all domain titles.
        if (usersUI && (usersUI.schoolUuid || usersUI.subgroupUuid)) {
          this.fetchDomainUsers();
        }
      }
    },

  },
};
</script>
<style scoped>
legend {
  font-size: 1.4rem;
  font-weight: bold;
}

.schools-filter-controls {
  margin-bottom: 10px;
}

.filter-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.filter-options .checkbox {
  margin: 0;
}

.selected-count {
  color: #555;
  font-size: 0.95em;
}

.schools-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 4px;
}

.custom-title-section {
  margin-top: 10px;
}

.selected-titles {
  margin-top: 10px;
}

.title-chip {
  margin-right: 4px;
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
}

.btn-close-chip {
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 4px;
  padding: 0;
  color: inherit;
  line-height: 1;
}

.btn-close-chip:focus {
  outline: 2px solid #66afe9;
  outline-offset: 1px;
  border-radius: 2px;
}

.input-mode-toggle {
  margin-bottom: 10px;
}

.input-mode-toggle .btn {
  margin-right: 4px;
}

.school-paste-section,
.title-paste-section {
  margin-top: 8px;
}

.school-paste-input,
.title-paste-input {
  margin-bottom: 8px;
}

.school-paste-apply,
.title-paste-apply {
  margin-bottom: 8px;
}

.school-paste-unmatched-list {
  margin: 6px 0 0;
  padding-left: 20px;
}
</style>